import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { TeamUser } from '../entities/TeamUser';
import { Subject } from 'rxjs';
import { InFilter } from '../baseServices/filter.interface';
import { StoreName } from '../entities/StoreName';
import { DataChangeEmitPayload, DataChangeEmitWho, DataChangeEmitWhat } from '../models/data-change-emit/data-change-emit-payload';
import { DbTransaction } from '../models/db/db-transaction';
import { Callback, CallbackNames } from '../models/db/db-transaction-callback';
import { EntityHelper, Check } from '../utils/entity-helper';
import { RecordNotFoundException, RecordSoftDeletedException } from '../utils/exceptions';
import { GlobalConstants } from '../constant/constant';

@Injectable({
  providedIn: 'root',
})
export class TeamUserService extends DataService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<TeamUser>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor() {
    super();
  }

  // #region Utility

  emitChange(teamUser: TeamUser, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: teamUser, who: who, what: what });
  }

  getEmitChangeCallback(teamUser: TeamUser, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForTeamUser + '_' + teamUser.RecordId,
      fn: this.emitChange.bind(this, teamUser, who, what),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.Team2User], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getTeamUserByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TeamUser> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<TeamUser>(StoreName.Team2User, onlineId, TeamUser, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as TeamUser;

    if (this.checkIsTeamUserSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getTeamUserByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TeamUser> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<TeamUser>(StoreName.Team2User, recordId, TeamUser, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as TeamUser;

    if (this.checkIsTeamUserSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getAllTeamUsers(filter: any = undefined, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TeamUser[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAll<TeamUser>(StoreName.Team2User, filter, TeamUser, transaction);

    let records = result as TeamUser[];

    if (!includeDeleted) {
      records = records.filter(teamUser => !this.checkIsTeamUserSoftDeleted(teamUser));
    }

    return records;
  }

  async getTeamUsersByOnlineIds(onlineIds: number[], existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TeamUser[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const filter: InFilter = { in: { field: 'Id', searchArray: onlineIds } };

    const result = await this.repository.getAll<TeamUser>(StoreName.Team2User, filter, TeamUser, transaction);

    let records = result as TeamUser[];

    if (!includeDeleted) {
      records = result.filter(teamUser => !this.checkIsTeamUserSoftDeleted(teamUser));
    }

    return records;
  }

  // #endregion
  // region Low-level Create

  private async createTeamUser(who: DataChangeEmitWho, recordToCreate: TeamUser, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LC1. Validate action
    await this.validateTeamUserCreation(transaction);

    // TODO: Impelment LC2 and LC3

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.Team2User, transaction);

    // LC5. Get created record
    const createdRecord = await this.getTeamUserByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // region Low-level Update

  async updateTeamUser(who: DataChangeEmitWho, recordToUpdate: TeamUser, skipLocalCopyValidation: boolean, localCopy: TeamUser | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LU1. Get existing record
    const existingRecord = await this.getTeamUserByRecordId(recordToUpdate.RecordId, transaction);

    // LU2. Validate action
    await this.validateTeamUserUpdate(recordToUpdate, transaction);

    // LU3. Idempotent check
    if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
      return;
    }

    // LU4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<TeamUser>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<TeamUser>(localCopy, existingRecord, checks);
    }

    // LU5. Validate model
    await this.validateTeamUser(recordToUpdate, transaction);

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.Team2User, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getTeamUserByRecordId(recordToUpdate.RecordId, transaction);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // region Low-level Delete

  // #endregion
  // region Low-level Validate Model

  async validateTeamUser(teamUser: TeamUser, existingTransaction?: DbTransaction): Promise<void> {
    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Low-level Validate Action

  async validateTeamUserCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  async validateTeamUserUpdate(teamUser: TeamUser, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Mid-level Get Many

  async getAllTeamUsersByTeamId(teamId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TeamUser[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<TeamUser>(StoreName.Team2User, GlobalConstants.KEY_NAMES.TeamId, teamId, TeamUser, transaction);

    let records = result as TeamUser[];

    if (!includeDeleted) {
      records = records.filter(teamUser => !this.checkIsTeamUserSoftDeleted(teamUser));
    }

    return records;
  }

  // #endregion
  // region Mid-level Create

  // #endregion
  // region Mid-level Update

  // #endregion
  // region Mid-level Delete

  // #endregion
  // region Mid-level Validate

  // #endregion
  // region Mid-level Upsert

  // #endregion
  // region Mid-level Sync

  async upsertTeamUserFromSync(who: DataChangeEmitWho, recordToUpsert: TeamUser, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getTeamUserByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateTeamUser(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createTeamUser(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // region Check

  private checkIsTeamUserSoftDeleted(record: TeamUser): boolean {
    return record.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: TeamUser, b: TeamUser): boolean {
    return a.TeamId === b.TeamId && a.UserId === b.UserId;
  }

  // #endregion
  // region Lookup

  // #endregion
  // region Transform

  // #endregion
  // region Other

  // #endregion
}
